import { FC, useCallback, useMemo, useState } from 'react';
import RoundTemplateSelect from '../../../round/header/RoundTemplateSelect';
import { useGetMasterTemplatesQuery } from '../../../../redux/apis/Templates.api';
import {
  Filter,
  ICondensedMasterTemplate
} from '../../../../config/interfaces';
import { useDebounceValue } from '../../../../utils/debouncers';
import { BlueChip } from '../../../shared/chips/blueChip';
import { ChipWrapper } from '../../feedbackModal/FeedbackModal.styles';

export interface ITemplateMultiSelectSectionProps {
  filterValues: Filter;
  setFilterValues: (data: Filter) => void;
}
const TemplateMultiSelectSection: FC<ITemplateMultiSelectSectionProps> = ({
  filterValues,
  setFilterValues
}) => {
  const [open, setOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const {
    data: masterTemplatesData,
    // isError: isGetMasterTemplatesError,
    // isFetching: isGetMasterTemplatesFetching,
    isLoading: isGetMasterTemplatesLoading
  } = useGetMasterTemplatesQuery({ archived: true, condensed: true });

  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounceValue(query, 250);

  const masterTemplateOptions = useMemo(() => {
    const allTemplates =
      masterTemplatesData
        ?.filter(template => template.roundingType === 'employee')
        .filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              t =>
                t.name.trim().toLowerCase() === item.name.trim().toLowerCase()
            )
        ) || [];
    if (filterValues.templateNames) {
      return allTemplates.filter(
        (template: ICondensedMasterTemplate) =>
          !filterValues.templateNames?.some(
            name =>
              name.trim().toLowerCase() === template.name.trim().toLowerCase()
          )
      );
    }
    return allTemplates;
  }, [masterTemplatesData, filterValues]);

  const handleTemplatesSelect = (
    template: ICondensedMasterTemplate | string
  ) => {
    const templateName =
      typeof template === 'string' ? template : template.name;
    const normalized = templateName.trim().toLowerCase();
    const current = filterValues.templateNames || [];
    const exists = current.some(t => t.trim().toLowerCase() === normalized);
    const updated = exists
      ? current.filter(t => t.trim().toLowerCase() !== normalized)
      : [...current, templateName];
    setFilterValues({
      ...filterValues,
      templateNames: updated
    });
  };

  const handleTypeAheadChange = useCallback(
    (e: any) => setQuery(e.target.value),
    []
  );

  const searchResults = useMemo(() => {
    switch (activeSearch) {
      case 'round-template-multi-select':
        return masterTemplateOptions.filter(
          (template: ICondensedMasterTemplate) =>
            template.name
              .toLowerCase()
              .includes(debouncedQuery.trim().toLowerCase())
        );
      default:
        return undefined;
    }
  }, [activeSearch, masterTemplateOptions, debouncedQuery]);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: '20rem' }}>
        {filterValues.templateNames?.map(template => (
          <ChipWrapper
            key={template}
            onClick={() => handleTemplatesSelect(template)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleTemplatesSelect(template);
              }
            }}
          >
            <BlueChip key={template} name={template} />
          </ChipWrapper>
        ))}
      </div>
      <div>
        <RoundTemplateSelect
          closeOnClick={false}
          disabled={isGetMasterTemplatesLoading}
          disabledWithTransparent={false}
          focused={activeSearch === 'round-template-multi-select'}
          name="round-template-multi-select"
          open={open}
          options={masterTemplateOptions}
          placeholder="Select templates"
          query={query}
          searchResults={searchResults}
          styles={{ paddingTop: '3px' }}
          typeAhead
          handleTemplateSelect={handleTemplatesSelect}
          setActiveSearch={setActiveSearch}
          setOpen={setOpen}
          setQuery={setQuery}
          setTypeAhead={setActiveSearch}
          setTypeAheadChange={handleTypeAheadChange}
        />
      </div>
    </div>
  );
};

export default TemplateMultiSelectSection;
